import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import { HomePage } from './pages/HomePage';
import { webSocketService } from './services/websocket';
import { GameState, Player } from './types';
import { GamePage } from './pages/GamePage';
import { LobbyPage } from './pages/LobbyPage';

function App() {
  const [view, setView] = useState<'home' | 'lobby' | 'game'>('home');
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [currentPlayerId, setCurrentPlayerId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDisconnected, setIsDisconnected] = useState(false);

  const connectWebSocket = useCallback((roomCode: string, playerName: string, roomName?: string, playerId?: string) => {
    webSocketService.connect(roomCode, playerName, roomName, playerId);
  }, []);

  useEffect(() => {
    webSocketService.onGameStateUpdate = (newGameState) => {
      if (isDisconnected) setIsDisconnected(false); // Reconnected
      setGameState(prevState => prevState ? { ...prevState, ...newGameState } : newGameState);

      const newPhase = newGameState.gamePhase;
      if (newPhase) {
        if (['drawing', 'voting', 'results'].includes(newPhase) && view !== 'game') setView('game');
        else if (newPhase === 'lobby' && view !== 'lobby') setView('lobby');
      }

      if (!currentPlayerId) {
        const name = localStorage.getItem("playerName");
        const player = newGameState.players?.find((p: Player) => p.name === name);
        if(player) setCurrentPlayerId(player.id);
      }
    };

    webSocketService.onDisconnect = () => {
      setIsDisconnected(true);
    };

    return () => {
      webSocketService.onGameStateUpdate = null;
      webSocketService.onDisconnect = null;
    };
  }, [currentPlayerId, view, isDisconnected]);

  // Prevent accidental navigation during game
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = ''; // Required for modern browsers
    };

    if (gameState && gameState.gamePhase !== 'lobby') {
      window.addEventListener('beforeunload', handleBeforeUnload);
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [gameState]);

  // Reconnection logic
  useEffect(() => {
    if (isDisconnected) {
      const interval = setInterval(() => {
        console.log("Attempting to reconnect...");
        const roomCode = gameState?.roomId;
        const playerName = localStorage.getItem("playerName");
        if (roomCode && playerName && currentPlayerId) {
          connectWebSocket(roomCode, playerName, undefined, currentPlayerId);
        }
      }, 5000); // Try every 5 seconds
      return () => clearInterval(interval);
    }
  }, [isDisconnected, gameState, currentPlayerId, connectWebSocket]);

  const handleCreateRoom = async (playerName: string, roomName: string) => {
    setIsLoading(true);
    try {
      localStorage.setItem("playerName", playerName);
      const response = await fetch(`${(process.env.REACT_APP_API_URL || '')}/api/rooms/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerName, roomName }),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      const { roomCode } = data;
      connectWebSocket(roomCode, playerName, roomName);
      setView('lobby');
    } catch (error) {
      console.error("Failed to create room:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoinRoom = async (playerName: string, roomCode: string) => {
    setIsLoading(true);
    try {
      localStorage.setItem("playerName", playerName);
      const response = await fetch(`${(process.env.REACT_APP_API_URL || '')}/api/rooms/join/${roomCode}`);
      if (!response.ok) {
        if (response.status === 404) alert("找不到房間！");
        else throw new Error(`HTTP error! status: ${response.status}`);
        return;
      }
      connectWebSocket(roomCode, playerName);
      setView('lobby');
    } catch (error) {
      console.error("Failed to join room:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReady = () => webSocketService.sendMessage({ type: 'playerReady' });
  const handleStartGame = () => webSocketService.sendMessage({ type: 'startGame' });

  const renderContent = () => {
    if (isDisconnected) return <h4>連線中斷，正在嘗試重新連線...</h4>;
    if (view === 'game' && gameState && currentPlayerId) return <GamePage gameState={gameState} currentPlayerId={currentPlayerId} />;
    if (view === 'lobby' && gameState && currentPlayerId) return <LobbyPage gameState={gameState} currentPlayerId={currentPlayerId} onReady={handleReady} onStartGame={handleStartGame} />;
    return <HomePage onCreateRoom={handleCreateRoom} onJoinRoom={handleJoinRoom} isLoading={isLoading} />;
  };

  const Scoreboard = () => {
    const [isExpanded, setIsExpanded] = useState(false);
    const sortedPlayers = gameState?.players ? [...gameState.players].sort((a, b) => b.score - a.score) : [];
    const playersToShow = isExpanded ? sortedPlayers : sortedPlayers.slice(0, 2);
    return (
      <div className="scoreboard" onClick={() => setIsExpanded(!isExpanded)}>
          <h4>分數排行榜</h4>
          <ul>
              {playersToShow.map((p: Player) => (
                  <li key={p.id}>{p.name} {isExpanded && `: ${p.score}`}</li>
              ))}
          </ul>
          {!isExpanded && sortedPlayers.length > 2 && <li>...</li>}
      </div>
    );
  };

  const RoomInfo = () => {
    if (!gameState) return null;
    return (
        <div className="room-info">
            房號: <span>{gameState.roomId}</span>
        </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <h1>Who Draw?</h1>
        <RoomInfo />
        {gameState && ['drawing', 'voting', 'results'].includes(gameState.gamePhase) && <Scoreboard />}
        {renderContent()}
      </header>
    </div>
  );
}

export default App;
